import styled, { css } from 'styled-components';
import { applyStyleModifiers, type ModifiersConfig, type ModifierKeys } from 'styled-components-modifiers';
import { rgba } from 'polished';

// Import styles
import { flex, resetButtonStyles } from 'components/styles';

// Import types
import type { Modifiers } from 'types/views/styles';

const INPUT_MODIFIERS: Modifiers = {
	light: ({ theme, isError }) => css`
		height: 4.6rem;
		padding: 0 2.4rem;
		align-self: stretch;
		border: 1px solid ${isError ? theme.form.light.color.error : rgba(theme.form.light.bg.contrast, 0.12)};
		border-radius: 2.3rem;
		color: ${isError ? theme.form.light.color.error : theme.form.light.color.primary} !important;
		font-weight: ${theme.font.normal};
		font-size: 1.4rem;

		&:focus-visible {
			border-color: ${rgba(theme.palette.black, 0.8)};
			color: ${theme.form.light.color.primary} !important;
		}

		&:disabled {
			background: ${rgba(theme.form.light.bg.secondary, 0.1)};
		}

		&:not(:placeholder-shown) {
			color: ${isError ? theme.form.light.color.error : theme.form.light.color.primary};

			&:disabled {
				color: ${theme.form.light.color.secondary};
			}
		}

		&:not(:placeholder-shown) + span,
		&:focus + span {
			padding: 0 0.4rem;
			transform: translateX(-0.4rem) translateY(-2.3rem);
			background-color: ${theme.form.light.bg.primary};
			font-size: 1.2rem;
			color: ${isError ? theme.form.light.color.error : theme.form.light.color.secondary};
		}

		:-webkit-autofill + span {
			transform: translateX(-0.4rem) translateY(-2.3rem) !important;
		}
	`
};

const LABEL_MODIFIERS: Modifiers = {
	light: ({ theme }) => css`
		color: ${theme.form.light.color.secondary};
		left: 2.4rem;
		padding-left: 0rem;
		font-size: 1.4rem;
	`
};

const ERROR_MODIFIERS: Modifiers = {
	light: ({ theme }) => css`
		margin: 0.4rem 2.4rem 1.6rem;
		padding: 0;
		line-height: normal;
		letter-spacing: 0.06px;
		color: ${theme.form.light.color.error};
	`
};

export const InputContainer = styled.div`
	display: flex;
	flex-direction: column;
`;

export const InputWrapper = styled.div`
	position: relative;
	${flex()};
`;

export const InputStyled = styled.input<{ modifiers?: ModifierKeys; isError: boolean }>`
	width: 100%;
	height: 5rem;
	padding: 0 1.4rem;
	border: 1px solid ${({ theme, isError }) => (isError ? theme.form.color.error : rgba(theme.form.color.primary, 0.2))};
	border-radius: 0.4rem;
	background: none;
	font-size: 1.6rem;
	color: ${({ theme, isError }) => (isError ? theme.form.color.error : theme.form.color.primary)};
	font-weight: ${({ theme }) => theme.font.light};
	outline: none;

	&:focus-visible {
		border-color: ${({ theme }) => theme.form.color.highlight};
		color: ${({ theme }) => theme.form.color.primary} !important;
	}

	&:disabled {
		background: ${({ theme }) => rgba(theme.form.color.primary, 0.03)};
	}

	&:not(:placeholder-shown) {
		color: ${({ theme, isError }) => (isError ? theme.form.color.error : theme.form.color.primary)};

		&:disabled {
			color: ${({ theme }) => rgba(theme.form.color.primary, 0.6)};
		}
	}

	&:not(:placeholder-shown) + span,
	&:focus + span {
		padding: 0 0.5rem;
		transform: translateX(1rem) translateY(-2.3rem);
		background-color: ${({ theme }) => theme.form.bg.primary};
		font-size: 1.2rem;
		font-weight: ${({ theme }) => theme.font.normal};
		color: ${({ theme, isError }) => (isError ? theme.form.color.error : rgba(theme.form.color.primary, 0.6))};
	}

	:-webkit-autofill + span {
		transform: translateX(1rem) translateY(-2.3rem);
	}

	&:-webkit-autofill,
	&:-webkit-autofill:focus {
		transition:
			background-color 600000s 0s,
			color 600000s 0s;
	}

	${applyStyleModifiers(INPUT_MODIFIERS as ModifiersConfig)};
`;

export const Label = styled.span<{ modifiers?: ModifierKeys }>`
	position: absolute;
	left: 0;
	padding-left: 1.2rem;
	font-size: 1.6rem;
	color: ${({ theme }) => rgba(theme.form.color.primary, 0.6)};
	pointer-events: none;
	transition: all 0.15s ease-in-out;

	${applyStyleModifiers(LABEL_MODIFIERS as ModifiersConfig)};
`;

export const Error = styled.p<{ modifiers?: ModifierKeys }>`
	font-weight: ${({ theme }) => theme.font.normal};
	font-size: 1.2rem;
	color: ${({ theme }) => theme.form.color.error};
	line-height: 1.4rem;
	display: inline-block;
	padding: 0.4rem 0;

	${applyStyleModifiers(ERROR_MODIFIERS as ModifiersConfig)};
`;

export const IconButton = styled.button`
	${resetButtonStyles}
	${flex()};
	position: absolute;
	right: 2.4rem;
`;

export const HintWrapper = styled.div<{ isFulfilled: boolean; isError: boolean }>`
	${flex({ jc: 'flex-start' })}
	gap: 0.8rem;
	color: ${({ isFulfilled, isError, theme }) =>
		isFulfilled ? theme.form.light.color.success : isError ? theme.form.light.color.error : theme.form.light.color.hint};

	&:last-child {
		margin-bottom: 1rem;
	}
`;

export const Hint = styled.p`
	font-size: 1.2rem;
	font-style: normal;
	line-height: normal;
	letter-spacing: 0.06px;
`;
